import { AUTO, CANDIDATES } from "./delimiters";

// What the grid is drawn from, and what the line above it reports.
export interface CsvTable {
  // A title per position and never per name: two columns may be called the same thing, and a grid that dropped one
  // of them would be hiding a column the file holds — the rule /sql's own grid is keyed by.
  columns: string[];
  rows: string[][];
  // Which character the fields were actually split on, "Auto" being nobody's answer once it has been resolved.
  delimiter: string;
  // Rows that are not as wide as the widest one, which the grid pads out and would otherwise say nothing about.
  ragged: number;
  // A quote nothing closed, which takes the rest of the document into the field that opened it.
  unterminated: boolean;
}

// The whole of what the page reads a document as: the delimiter settled, the records split, and the first of them
// taken as titles or not. Nothing here renders and nothing here is asynchronous, so `tests/csv.test.ts` drives it
// as strings.
export function readCsv(text: string, choice: string, header: boolean): CsvTable {
  const declared = declaredDelimiter(text);
  const body = declared ? text.slice(declared.line.length) : text;
  // A delimiter somebody picked outranks the file's own word for it, and the file's word outranks any guess.
  const delimiter = choice !== AUTO ? choice : declared ? declared.delimiter : sniffDelimiter(body);
  const { records, unterminated } = parseRecords(body, delimiter);

  // The widest row and not the header's own width: a body row with more fields than titles is a column too, and
  // naming it is the only alternative to leaving it off screen.
  const width = records.reduce((most, record) => Math.max(most, record.length), 0);
  const titles = header ? records[0] ?? [] : [];
  const rows = header ? records.slice(1) : records;

  return {
    columns: Array.from({ length: width }, (_, at) => columnTitle(titles[at], at)),
    rows,
    delimiter,
    ragged: rows.filter((row) => row.length !== width).length,
    unterminated,
  };
}

// Excel's `sep=;` first line says what the fields are split on, so it is a declaration about the records and not one.
function declaredDelimiter(text: string): { delimiter: string; line: string } | null {
  const match = SEP_LINE.exec(text);
  return match ? { delimiter: match[1], line: match[0] } : null;
}

// A header cell that is blank names nothing, so the position stands in for it — as it does for every column when
// there is no header row at all.
export function columnTitle(name: string | undefined, at: number): string {
  return name?.trim() || `Column ${at + 1}`;
}

// RFC 4180 as far as it goes, and as forgiving as a viewer has to be past that: CRLF and LF alike end a record, a
// quote is only a quote where a field begins, a doubled quote inside one is a quote of the value, and a quote left
// open runs to the end of the document rather than making the whole read a failure.
export function parseRecords(text: string, delimiter: string): CsvRecords {
  // A byte order mark is the encoding's own and not the first letter of the first title.
  const body = text.charCodeAt(0) === BOM ? text.slice(1) : text;
  const records: string[][] = [];

  let record: string[] = [];
  let field = "";
  let quoted = false;
  let inQuotes = false;
  let at = 0;

  // A line with one empty field on it is a blank line rather than a row, which is the one thing a file is full of
  // that nobody meant to put in it — unless the quotes are there, which is somebody writing an empty row down.
  const endRecord = () => {
    record.push(field);
    field = "";
    if (record.length > 1 || record[0] !== "" || quoted) records.push(record);
    record = [];
    quoted = false;
  };

  while (at < body.length) {
    const char = body[at];

    if (inQuotes) {
      if (char !== "\"") {
        field += char;
        at++;
      } else if (body[at + 1] === "\"") {
        field += "\"";
        at += 2;
      } else {
        inQuotes = false;
        at++;
      }
      continue;
    }

    // Only where the field begins: a quote in the middle of one is a character somebody typed.
    if (char === "\"" && field === "") {
      inQuotes = true;
      quoted = true;
      at++;
    } else if (char === delimiter) {
      record.push(field);
      field = "";
      at++;
    } else if (char === "\n" || char === "\r") {
      endRecord();
      at += char === "\r" && body[at + 1] === "\n" ? 2 : 1;
    } else {
      field += char;
      at++;
    }
  }

  // The last record, which a document not ending on a newline still has one of.
  if (field !== "" || record.length > 0 || inQuotes || quoted) endRecord();

  return { records, unterminated: inQuotes };
}

export interface CsvRecords {
  records: string[][];
  unterminated: boolean;
}

// Reads the head of the document under each candidate and takes the one that splits it evenly: a delimiter that is
// really there gives every row the same number of fields, and one that is not gives every row one.
export function sniffDelimiter(text: string): string {
  const head = text.slice(0, SNIFF_LIMIT);
  let best = CANDIDATES[0];
  let score = 0;

  for (const candidate of CANDIDATES) {
    const records = parseRecords(head, candidate).records.slice(0, SNIFF_ROWS);
    if (records.length === 0) continue;

    const width = records[0].length;
    // One field a row is a delimiter the document does not use, whether or not it is used consistently.
    if (width < 2) continue;

    // An even split is worth more than any uneven one, however many fields the uneven one found.
    const even = records.every((record) => record.length === width);
    const candidateScore = even ? EVEN_SPLIT + width : width;
    if (candidateScore > score) {
      best = candidate;
      score = candidateScore;
    }
  }

  return best;
}

const BOM = 0xfeff;

// One character and then the end of the line, which is the whole of the form; `sep=;;` is a record like any other.
const SEP_LINE = /^\uFEFF?sep=([^\r\n])(?:\r\n|\r|\n|$)/i;

// Enough of the document to tell one delimiter from another, and little enough that sniffing four of them over a
// file of several megabytes is not the thing that makes an upload slow.
const SNIFF_LIMIT = 64 * 1024;
const SNIFF_ROWS = 20;

// Past any field count a row could actually have, so an even split always outranks an uneven one.
const EVEN_SPLIT = 1000;
